'use client';

// Global mount of the unified SidePanel — the fixed right-side overlay on
// stock/ETF (and other) pages, toggled from the nav. Same component the Pro
// Terminal uses inline; here it floats over the page, closes with an X, and
// reserves layout width via WatchlistPanelContext so the page content reflows
// (see LayoutShell). Replaces the legacy WatchlistSidePanel.

import React, { useMemo } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { useAuth } from '@/context/AuthContext';
import { useWatchlistPanel } from '@/context/WatchlistPanelContext';
import { TickerPopupProvider } from '@/components/ticker/TickerPopupProvider';
import SidePanel from './SidePanel';

export default function GlobalSidePanel() {
  const { user } = useAuth();
  const { isOpen, close, width, setWidth, tradePrefill } = useWatchlistPanel();
  const pathname = usePathname();
  const router = useRouter();

  const symbol = useMemo(() => {
    const m = /^\/(?:stocks|etf)\/([A-Za-z][A-Za-z.\-]{0,7})/.exec(pathname || '');
    return m ? m[1].toUpperCase() : '';
  }, [pathname]);

  // The page chart publishes its current price + (replay/historical) as-of via
  // the panel context. Only honor it when it's for THIS page's symbol, so the
  // Trade tab prefills the live quote in normal mode and the paused-replay /
  // historical-day price (+ its time) in replay mode. Was hardcoded null →
  // empty Price field + a NOW() trade-time even while replaying.
  const prefillForSymbol = tradePrefill && symbol && tradePrefill.symbol === symbol ? tradePrefill : null;

  if (!isOpen || !user) return null;

  return (
    <TickerPopupProvider>
      <div className="fixed inset-0 top-14 z-30 bg-black/50 md:hidden" onClick={close} aria-hidden />
      <div
        className="fixed top-14 right-0 bottom-0 z-40 w-full md:w-[var(--wl-w)] shadow-2xl"
        style={{ '--wl-w': `${width}px` } as React.CSSProperties}
      >
        <SidePanel
          symbol={symbol}
          lastPrice={prefillForSymbol?.price ?? null}
          asOfIso={prefillForSymbol?.asOfIso ?? null}
          asOfLabel={prefillForSymbol?.asOfLabel ?? null}
          onSelectSymbol={(s) => router.push(`/stocks/${s}`)}
          onDismiss={close}
          dismissMode="close"
          width={width}
          onWidthChange={setWidth}
          tickerMode="popup"
        />
      </div>
    </TickerPopupProvider>
  );
}
